import React from 'react';
import { motion, AnimatePresence } from "motion/react";
import { Toaster } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { useProfile } from '@/features/user/hooks/useProfile';
import { useProfileTickets } from '@/features/user/hooks/useProfileTickets';
import { useProfilePurchases } from '@/features/user/hooks/useProfilePurchases';
import { useProfileFavourites } from '@/features/user/hooks/useProfileFavourites';
import { useProfileView } from '@/features/user/hooks/useProfileView';
import { useAuth } from '@/hooks/useAuth';
import { ProfileSidebar, ProfileDetails } from '@/features/user/components';
import { MyTicketsSection } from '@/features/booking/components/MyTicketsSection';
import { PreviousPurchasesSection } from '@/features/cart/components/PreviousPurchasesSection';
import FavouritesSection from '@/features/user/components/FavouritesSection';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { StatusTestProvider, GlobalStatusSwitcher } from '@/features/booking/components/BookingStatusSwitcher';

const ProfilePage: React.FC = () => {
  const {
    user,
    setUser,
    isEditing,
    setIsEditing,
    profilePic,
    handleFileChange,
    handleSaveChanges
  } = useProfile();

  const { user: authUser, isLoading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();

  // Get initial tab from URL
  const getInitialTab = (): 'by-event' | 'by-cart' | 'by-transfer' => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'transfer') return 'by-transfer';
    if (tabParam === 'cart') return 'by-cart';
    return 'by-event';
  };

  // Use custom hooks for tickets and purchases
  const {
    groupedByEventSchedules,
    groupedByPastEventSchedules,
    groupedByCarts,
    transfers,
    ticketsLoading,
    ticketsError,
    pastEventsLoading,
    activeTab,
    setActiveTab,
    activeEventTab,
    fetchTickets,
    handleEventTabChange,
  } = useProfileTickets(getInitialTab());

  const {
    completedCarts,
    cartsLoading,
    cartsError,
    fetchCompletedCarts,
  } = useProfilePurchases();

  const {
    likedClubs,
    likedEvents,
    likedArtists,
    loading: favouritesLoading,
    error: favouritesError,
    fetchFavourites,
    unlikedClubIds,
    unlikedEventIds,
    unlikedArtistIds,
    handleClubLikeToggle,
    handleEventLikeToggle,
    handleArtistLikeToggle,
  } = useProfileFavourites();

  const {
    activeView,
    handleViewChange,
    validationError,
  } = useProfileView(fetchTickets, fetchCompletedCarts, fetchFavourites);

  if (validationError && !authUser) {
    return (
      <motion.div
        className="bg-black text-white min-h-screen font-sans flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            delay: 0.2,
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
        >
          <motion.div
            className="text-red-400 text-xl mb-4"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              delay: 0.4,
              type: "spring",
              stiffness: 300,
              damping: 20
            }}
          >
            ⚠️
          </motion.div>
          <motion.p
            className="text-red-400 mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
          >
            Failed to load profile
          </motion.p>
          <motion.p
            className="text-gray-400 text-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
          >
            {validationError}
          </motion.p>
          <motion.button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{
              scale: 1.05,
              boxShadow: "0 10px 25px rgba(37, 99, 235, 0.3)"
            }}
            whileTap={{ scale: 0.95 }}
            transition={{
              delay: 0.7,
              duration: 0.4,
              type: "spring",
              stiffness: 400,
              damping: 10
            }}
          >
            Retry
          </motion.button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <StatusTestProvider>
      <motion.div
        className="bg-black text-white min-h-screen font-sans"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: { background: '#333', color: '#fff' },
        }}
      />
      <Header />
      <main className="pt-28 pb-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h1
            className="text-4xl md:text-5xl font-serif font-medium mb-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.2,
              type: "spring",
              stiffness: 300,
              damping: 30
            }}
          >
            My Profile
          </motion.h1>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <ProfileSidebar
              user={user}
              profilePic={profilePic}
              onFileChange={handleFileChange}
              activeView={activeView}
              onViewChange={handleViewChange}
              loading={authLoading}
            />

            {/* Right side content based on active view */}
            <AnimatePresence mode="wait">
              {activeView === 'profile' && (
                <motion.div
                  key="profile"
                  className="lg:col-span-2"
                  initial={{ opacity: 0, x: 50, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -50, scale: 0.95 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                    mass: 0.8
                  }}
                >
                  <ProfileDetails
                    user={user}
                    setUser={setUser}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    onSaveChanges={handleSaveChanges}
                  />
                </motion.div>
              )}

              {activeView === 'tickets' && (
                <motion.div
                  key="tickets"
                  className="lg:col-span-2"
                  initial={{ opacity: 0, x: 50, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: -50, scale: 0.95 }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                    mass: 0.8
                  }}
                >
                  {ticketsError ? (
                    <motion.div
                      className="lg:col-span-2 flex items-center justify-center h-64"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <div className="text-center">
                        <motion.p
                          className="text-red-400 mb-4"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                        >
                          {ticketsError}
                        </motion.p>
                        <motion.button
                          onClick={fetchTickets}
                          className="px-6 py-2 bg-violet-600 text-white rounded-full hover:bg-violet-500 transition-colors"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          whileHover={{
                            scale: 1.05,
                            boxShadow: "0 10px 25px rgba(139, 92, 246, 0.3)"
                          }}
                          whileTap={{ scale: 0.95 }}
                          transition={{
                            delay: 0.2,
                            type: "spring",
                            stiffness: 400,
                            damping: 10
                          }}
                        >
                          Try Again
                        </motion.button>
                      </div>
                    </motion.div>
                  ) : (
                    <MyTicketsSection
                      groupedByEventSchedules={groupedByEventSchedules}
                      groupedByPastEventSchedules={groupedByPastEventSchedules}
                      groupedByCarts={groupedByCarts}
                      allTransfers={transfers}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                      activeEventTab={activeEventTab}
                      onEventTabChange={handleEventTabChange}
                      pastEventsLoading={pastEventsLoading}
                      loading={ticketsLoading}
                    />
                  )}
                </motion.div>
              )}

              {activeView === 'purchases' && (
                <PreviousPurchasesSection
                  carts={completedCarts}
                  loading={cartsLoading}
                  error={cartsError}
                  onRetry={fetchCompletedCarts}
                />
              )}

              {activeView === 'favourites' && (
                <FavouritesSection
                  likedClubs={likedClubs}
                  likedEvents={likedEvents}
                  likedArtists={likedArtists}
                  loading={favouritesLoading}
                  error={favouritesError}
                  unlikedClubIds={unlikedClubIds}
                  unlikedEventIds={unlikedEventIds}
                  unlikedArtistIds={unlikedArtistIds}
                  onClubLikeToggle={handleClubLikeToggle}
                  onEventLikeToggle={handleEventLikeToggle}
                  onArtistLikeToggle={handleArtistLikeToggle}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>
      <GlobalStatusSwitcher />
      <Footer />
    </motion.div>
    </StatusTestProvider>
  );
};

export default ProfilePage;
